import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';

import { InputText, Text, type InputTextProps } from '@companycam/slab-web';

import GoogleLogo from 'images/google-full.png';
import { translate } from 'translations/i18n';
import {
  useGooglePlacesAutocomplete,
  useGetPlaceDetails,
} from 'data/v3/googlePlaces';
import {
  type GooglePlacesAutocomplete,
  type GooglePlaceDetails,
} from 'data/v3/data-contracts';

const GoogleInputContainer = styled.div`
  width: 100%;
`;

const GoogleAttributionContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_m)
    var(--cc_size_spacing_s);
  border-top: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
  align-items: center;
  gap: var(--cc_size_spacing_xs);
  margin-top: var(--cc_size_spacing_s);
`;

const GoogleAttribution = styled.img`
  height: 16px;
  margin-top: 2px;
`;

const AutocompleteResultsContainer = styled.div``;

const AutocompleteResults = styled.div`
  position: absolute;
  background: var(--cc_color_background_1);
  border: 1px solid var(--cc_color_border_default);
  border-radius: var(--cc_size_border_radius_l);
  width: 100%;
  max-width: 536px;
  padding: var(--cc_size_spacing_s);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: var(--cc_size_spacing_s);
`;

const AutocompleteResult = styled.div.attrs(() => ({
  tabIndex: 0,
  role: 'option',
}))`
  padding: calc(var(--cc_size_spacing_s) + var(--cc_size_spacing_xs))
    var(--cc_size_spacing_m);
  cursor: pointer;

  &:hover,
  &:focus {
    background: var(--cc_color_background_2);
    border-radius: var(--cc_size_border_radius_l);
  }
`;

const useDebouncedValue = (value: string) => {
  const [debouncedValue, _setDebouncedValue] = useState('');

  const setDebouncedValue = debounce(_setDebouncedValue, 300);
  useEffect(() => {
    setDebouncedValue(value);
  }, [value, setDebouncedValue]);

  return debouncedValue;
};

type GooglePlaceAutocompleteProps = Omit<
  InputTextProps,
  'id' | 'placeholder' | 'testId' | 'label' | 'value' | 'onChange'
> & {
  id?: InputTextProps['id'];
  label?: InputTextProps['label'];
  placeholder?: InputTextProps['placeholder'];
  testId?: InputTextProps['testId'];
  onPlaceSelect: (place: GooglePlaceDetails) => void;
  primaryType?: string;
  status?: InputTextProps['status'];
};

export const GooglePlaceAutocomplete = ({
  onPlaceSelect,
  primaryType = 'establishment',
  ...props
}: GooglePlaceAutocompleteProps) => {
  // we have two values here because we want the input value to update immediately but only want to
  // periodically update the debounced value that makes the API calls
  const [value, setValue] = useState('');
  const [placeId, setPlaceId] = useState<string>();

  const debouncedValue = useDebouncedValue(value);

  const { data: placeDetails } = useGetPlaceDetails(placeId);
  const googlePlaceSuggestion = useGooglePlacesAutocomplete(
    debouncedValue,
    primaryType,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  // once place details are selected move on
  useEffect(() => {
    if (placeDetails) {
      onPlaceSelect(placeDetails.data);
      setPlaceId(undefined);
    }
  }, [placeDetails, onPlaceSelect]);

  const handleClick = (selectedPlaceId: string) => {
    setPlaceId(selectedPlaceId);
    setValue('');
    // calling refetch here isn't always accurate so wait to refetch until after we set the value
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLDivElement>,
    selectedPlaceId: string,
  ) => {
    if (event.key === 'Enter') {
      handleClick(selectedPlaceId);
    }
  };

  const suggestions = (googlePlaceSuggestion.data?.data.suggestions ||
    []) as GooglePlacesAutocomplete['suggestions'];

  return (
    <GoogleInputContainer>
      <InputText
        id={props.id || 'google-place-autocomplete-new'}
        value={value}
        onChange={handleChange}
        label={props.label}
        message={props.message}
        placeholder={props.placeholder}
        testId={props.testId || 'google-place-autocomplete'}
        status={props.status}
      />

      <AutocompleteResultsContainer>
        {suggestions.length > 0 && (
          <AutocompleteResults>
            {suggestions.map((place) => (
              <AutocompleteResult
                key={place.placePrediction.placeId}
                onClick={() => handleClick(place.placePrediction.placeId)}
                onKeyUp={(e) =>
                  handleKeyPress(e, place.placePrediction.placeId)
                }
              >
                <Text as="h3" family="heading" size="s" weight="600">
                  {place.placePrediction.structuredFormat?.mainText?.text}
                </Text>
                <Text as="h3" family="heading" size="s" color="nonessential">
                  {place.placePrediction.structuredFormat?.secondaryText?.text}
                </Text>
              </AutocompleteResult>
            ))}
            <GoogleAttributionContainer>
              <Text as="p" family="heading" size="xs" color="nonessential">
                {translate('project.reviews.customerFlow.poweredBy')}
              </Text>
              <GoogleAttribution
                src={GoogleLogo}
                alt={translate('project.reviews.reviewProfile.poweredByGoogle')}
              />
            </GoogleAttributionContainer>
          </AutocompleteResults>
        )}
      </AutocompleteResultsContainer>
    </GoogleInputContainer>
  );
};
